import { create } from "zustand";
import { applyCommissionAdjustment, createCommissionStep, updateCommissionStep } from "../../api-layer/commission-step-api/commissionApi";
import { usePricingStore } from "../pricing/pricingStore";
import { persianLanguage } from "../../../constants/persianTerms";
import { useRequestStatusStore } from "../requestStatusStore";

const SAVE_ERROR = `ذخیره ${persianLanguage.word.general.BUBBLE} انجام نشد. دوباره تلاش کنید.`;

function saveErrorMessage(error) {
    const status = error.response?.status;
    const data = error.response?.data;
    if (status < 400 || status >= 500 || !data) return SAVE_ERROR;

    if (typeof data === "string") return data.trim() || SAVE_ERROR;
    if (Array.isArray(data)) return data.filter(item => typeof item === "string").join("، ") || SAVE_ERROR;

    const messages = Object.entries(data).flatMap(([field, value]) => {
        const items = Array.isArray(value) ? value : [value];
        return items
            .filter(item => typeof item === "string" && item.trim())
            .map(item => ["detail", "message", "non_field_errors"].includes(field)
                ? item
                : `${field}: ${item}`);
    });
    return messages.join("، ") || SAVE_ERROR;
}

export const useCommissionStepStore = create((set, get) => ({
    stepsByCode: {},
    loadingByCode: {},
    loadFailedByCode: {},
    savingByCode: {},
    adjustingByCode: {},
    errorsByCode: {},

    clearError: code => set(state => ({
        errorsByCode: { ...state.errorsByCode, [code]: null },
    })),

    loadStep: qdCode => {
        if (!qdCode) return;
        const quantity = Object.values(usePricingStore.getState().quantitiesById || {})
            .find(item => item.code === qdCode);
        const failed = !quantity;
        const stepValue = quantity?.commissionStepValue;
        const step = stepValue == null ? null : {
            stepValue,
            commissionUnit: quantity.commissionUnit,
        };
        set(state => ({
            stepsByCode: failed ? state.stepsByCode : { ...state.stepsByCode, [qdCode]: step },
            loadingByCode: { ...state.loadingByCode, [qdCode]: false },
            loadFailedByCode: { ...state.loadFailedByCode, [qdCode]: failed },
            errorsByCode: {
                ...state.errorsByCode,
                [qdCode]: failed ? `بارگذاری ${persianLanguage.word.general.BUBBLE} انجام نشد.` : null,
            },
        }));
    },

    saveStep: async (code, stepValue) => {
        const existing = get().stepsByCode[code];
        const requestKey = `commission-step-${code}`;
        const { startRequest, finishRequest } = useRequestStatusStore.getState();
        const requestId = startRequest(requestKey, code, existing?.stepValue ?? null, stepValue, {
            type: "commission-step", code,
        });
        set(state => ({
            savingByCode: { ...state.savingByCode, [code]: true },
            errorsByCode: { ...state.errorsByCode, [code]: null },
        }));
        try {
            const response = existing
                ? await updateCommissionStep(code, { stepValue })
                : await createCommissionStep(code, { stepValue, commissionUnit: 1 });
            set(state => ({
                stepsByCode: { ...state.stepsByCode, [code]: response.data },
                errorsByCode: { ...state.errorsByCode, [code]: null },
            }));
            finishRequest(requestKey, requestId, "success");
            return response.data;
        } catch (error) {
            set(state => ({ errorsByCode: { ...state.errorsByCode, [code]: saveErrorMessage(error) } }));
            finishRequest(requestKey, requestId, "error", saveErrorMessage(error));
            throw error;
        } finally {
            set(state => ({ savingByCode: { ...state.savingByCode, [code]: false } }));
        }
    },

    adjust: async (code, adjustment) => {
        const quantity = Object.values(usePricingStore.getState().quantitiesById || {})
            .find(item => item.code === code);
        const requestKey = `commission-adjustment-${code}`;
        const { startRequest, finishRequest, updateRequestMetadata } = useRequestStatusStore.getState();
        const requestId = startRequest(requestKey, quantity?.title ?? code, null, null, {
            type: "commission-adjustment", code, quantityId: quantity?.nodeId,
            target: adjustment.target, operation: adjustment.operation, stepCount: adjustment.stepCount,
        });
        set(state => ({
            adjustingByCode: { ...state.adjustingByCode, [code]: true },
            errorsByCode: { ...state.errorsByCode, [code]: null },
        }));
        try {
            const response = await applyCommissionAdjustment(code, adjustment);
            const result = response.data;
            const sides = adjustment.target === "both" ? ["buy", "sell"] : [adjustment.target];
            const expectedCommissions = Object.fromEntries(sides.map(side => [side, result[`${side}Commission`]]));
            updateRequestMetadata(requestKey, requestId, {
                type: "commission-adjustment", code, quantityId: quantity?.nodeId,
                target: adjustment.target, operation: adjustment.operation, stepCount: adjustment.stepCount,
                expectedCommissions,
            });
            finishRequest(requestKey, requestId, "success");
        } catch {
            set(state => ({ errorsByCode: { ...state.errorsByCode, [code]: "نتیجه تغییر کارمزد مشخص نیست؛ وضعیت فعلی را بررسی کنید." } }));
            finishRequest(requestKey, requestId, "error", "نتیجه تغییر کارمزد مشخص نیست؛ وضعیت فعلی را بررسی کنید.");
        } finally {
            set(state => ({ adjustingByCode: { ...state.adjustingByCode, [code]: false } }));
        }
    },
}));
